import {
  Action,
  combineReducers,
  configureStore,
  getDefaultMiddleware,
  ThunkAction,
} from "@reduxjs/toolkit";
import { createReduxHistoryContext } from "redux-first-history";

import { createBrowserHistory } from "history";
import createSagaMiddleware from "redux-saga";
import { fork } from "redux-saga/effects";
import { authReducer } from "./auth";
import { azureMapsReducer } from "./azureMaps";
import campaignSlice from "./campaign/campaignSlice";
import { clientsReducer, clientsSaga } from "./clients/clients";
import { locationsReducer, locationsSaga } from "./locations/locations";
import { settingsReducer, settingsSaga } from "./settings";
import { taxonomySaga } from "./taxonomy/sagas";
import { taxonomyReducer } from "./taxonomy/taxonomy";
import { userRoleReducer } from "./userRole";
import { usersReducer, usersSaga } from "./users";
import {
  valueMonitoringReducer,
  valueMonitoringSaga,
} from "./valueMonitoring/valueMonitoringPage";

const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({ history: createBrowserHistory() });

const reducer = combineReducers({
  taxonomy: taxonomyReducer,
  userRole: userRoleReducer,
  azureMaps: azureMapsReducer,
  settings: settingsReducer,
  clients: clientsReducer,
  auth: authReducer,
  users: usersReducer,
  valueMonitoring: valueMonitoringReducer,
  locations: locationsReducer,
  router: routerReducer,
  campaign: campaignSlice.reducer,
});

const sagaMiddleware = createSagaMiddleware();

export const store = configureStore({
  reducer,
  middleware: [
    ...getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(routerMiddleware),
    sagaMiddleware,
  ],
});

export type RootState = ReturnType<typeof reducer>;
export type AppDispatch = any;
export type AppThunk = ThunkAction<void, RootState, null, Action<string>>;
export type AppThunkPromise<T> = ThunkAction<
  Promise<T>,
  RootState,
  null,
  Action<string>
>;

export type LoadStatus = "none" | "loading" | "success" | "error";

export function* rootSaga() {
  yield fork(taxonomySaga);
  yield fork(settingsSaga);
  yield fork(usersSaga);
  yield fork(valueMonitoringSaga);
  yield fork(locationsSaga);
  yield fork(clientsSaga);
}

sagaMiddleware.run(rootSaga);

export const history = createReduxHistory(store);
export const selectRouterLocation = (s: RootState) => s.router.location;
